import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Home, ChevronDown } from "lucide-react";
import { categories, getToolsByCategory, CategoryId } from "@/lib/tools-data";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useSidebar } from "../ui/sidebar";

const categoryColors: Record<CategoryId, string> = {
  pdf: "text-category-pdf",
  image: "text-category-image",
  text: "text-category-text",
  calculator: "text-category-calculator",
  utility: "text-category-utility",
  mini: "text-category-mini",
};

export function AppSidebar() {
  const {  setOpenMobile, setOpen , openMobile } = useSidebar();
  console.log('openMobile: ', openMobile);

  const isMobile = window.innerWidth < 768;
  const [collapsed, setCollapsed] = useState(false);
  const [openCategories, setOpenCategories] = useState<CategoryId[]>([
    "pdf",
    "image",
  ]);
  const location = useLocation();

  const toggleCategory = (categoryId: CategoryId) => {
    setOpenCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r bg-sidebar transition-all duration-300 sticky top-0",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              UT
            </div>
            <span className="font-semibold text-foreground">UtilityBox</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className={cn("h-8 w-8", collapsed && "mx-auto")}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 scrollbar-thin">
        {/* Home Link */}
        <Link
          to="/"
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
            location.pathname === "/"
              ? "bg-primary text-primary-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent"
          )}
        >
          <Home className="h-5 w-5 shrink-0" />
          {!collapsed && <span>Home</span>}
        </Link>

        {/* Categories */}
        <div className="mt-4 space-y-1">
          {categories.map((category) => {
            const tools = getToolsByCategory(category.id);
            const isOpen = openCategories.includes(category.id);
            const hasActiveTool = tools.some(
              (t) => location.pathname === t.route
            );
            const Icon = category.icon;

            return (
              <Collapsible
                key={category.id}
                open={!collapsed && isOpen}
                onOpenChange={() => !collapsed && toggleCategory(category.id)}
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      hasActiveTool
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground hover:bg-sidebar-accent",
                      collapsed && "justify-center"
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-5 w-5 shrink-0",
                        categoryColors[category.id]
                      )}
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">
                          {category.name}
                        </span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            isOpen && "rotate-180"
                          )}
                        />
                      </>
                    )}
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-1 pt-1">
                  {tools.map((tool) => {
                    const ToolIcon = tool.icon;
                    const isActive = location.pathname === tool.route;

                    return (
                      <Link
                        key={tool.id}
                        to={tool.route}
                       onClick={() => {
  if (isMobile) {
    setOpenMobile(false); // closes sheet completely
  }
}}
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 pl-11 text-sm transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"
                        )}
                      >
                        <ToolIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{tool.name}</span>
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="border-t p-4">
          <p className="text-xs text-muted-foreground text-center">
            100% Free • No Sign-up
          </p>
        </div>
      )}
    </aside>
  );
}
